import { useApolloClient } from "@apollo/client";
import { CircularProgress, Icon } from "@mui/material";
import MDBox from "components/MDBox";
import UiButton from "components/base/UiButton";
import { Proposal, ProposalProduct, ProposalStage, ProposalStatus } from "generated/graphql";
import useCustomGetProposal from "hooks/proposals/useCustomGetProposal";
import useCustomAddProposalStage from "hooks/proposals/useCustomAddProposalStage";
import useDeleteProposalStage from "hooks/proposals/useDeleteProposalStage";
import useModalState from "hooks/useModalState";
import DashboardPage from "layouts/DashboardPage/DashboardPage";
import PendingChanges from "modules/PendingChanges/PendingChanges";
import ProposalContext from "modules/proposals/providers/ProposalContextProvider";
import ProposalFormsContextProvider from "modules/proposals/providers/ProposalFormsContextProvider";
import { draftColors } from "modules/proposals/draft/draftDesignTokens";
import DraftContext from "modules/proposals/draft/DraftContext";
import ReorderStagesDrawer from "modules/proposals/draft/ReorderStagesDrawer";
import Stage from "modules/proposals/draft/Stage";
import TopPanel from "modules/proposals/draft/TopPanel";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";
import { Link } from "react-router-dom";
import { getRoute } from "utils/routing";
import proposalProductFragment from "schema/fragments/proposalProductFragment";

export default function ProposalDraftPage({ forceReadOnly = false }: { forceReadOnly?: boolean }) {
  const { proposalId } = useParams();
  const client = useApolloClient();
  const { loading, data: proposal } = useCustomGetProposal({ id: proposalId });
  const [expandedStageId, setExpandedStageId] = useState<string | false>(false);
  const [expandedProductByStage, setExpandedProductByStage] = useState<
    Record<string, string | false>
  >({});
  const [addProposalStage] = useCustomAddProposalStage({ proposalId: proposalId ?? "" });
  const [deleteProposalStage] = useDeleteProposalStage();
  const reorderDrawer = useModalState();

  const handleDeleteStage = useCallback(
    async (id: string) => {
      await deleteProposalStage({ variables: { id } });
    },
    [deleteProposalStage]
  );

  const writeProposalProduct = useCallback(
    (proposalProduct: ProposalProduct) => {
      client.writeFragment({
        id: `ProposalProduct:${proposalProduct.id}`,
        fragment: proposalProductFragment,
        data: proposalProduct,
      });
    },
    [client]
  );

  const handleAddStage = useCallback(async () => {
    if (!proposalId) return;
    const result = await addProposalStage({ variables: { proposalId } });
    if (result?.data) {
      const stages = (result.data as any)?.customAddProposalStage?.stages;
      const newStage = stages?.[stages.length - 1];
      if (newStage) setExpandedStageId(newStage.id);
    }
  }, [addProposalStage, proposalId]);

  const stages = proposal?.stages ?? [];
  const isReadOnly = forceReadOnly || proposal?.status !== ProposalStatus.DRAFT;

  useEffect(() => {
    if (stages.length === 1 && expandedStageId === false) {
      const stage = stages[0];
      if (stage.name) setExpandedStageId(stage.id);
    }
  }, [stages]);

  if (loading || !proposal) {
    return (
      <DashboardPage>
        <MDBox
          display="flex"
          justifyContent="center"
          alignItems="center"
          sx={{ minHeight: "60vh" }}
        >
          <CircularProgress size={40} />
        </MDBox>
      </DashboardPage>
    );
  }

  const activeExpandedStage = expandedStageId;

  return (
    <DashboardPage>
      <DraftContext.Provider value={{ isReadOnly }}>
        <ProposalFormsContextProvider>
          <ProposalContext.Provider
            value={{
              proposal: proposal as Proposal,
              writeProposalProduct,
              inViewMode: isReadOnly,
            }}
          >
            <MDBox sx={{ backgroundColor: draftColors.pageBg, minHeight: "100%" }}>
              <MDBox mb={3}>
                <TopPanel proposal={proposal} />
              </MDBox>

              {stages.map((stage) => (
                <MDBox key={`${stage.id}:${expandedProductByStage[stage.id] ?? ""}`} mb={2}>
                  <Stage
                    stage={stage as ProposalStage}
                    proposalId={proposal.id}
                    availableStages={stages as ProposalStage[]}
                    isExpanded={activeExpandedStage === stage.id}
                    onToggleExpand={() =>
                      setExpandedStageId((prev) => (prev === stage.id ? false : stage.id))
                    }
                    onRequestExpand={() => setExpandedStageId(stage.id)}
                    initialExpandedProductId={expandedProductByStage[stage.id] ?? false}
                    onExpandProduct={(stageId, productId) => {
                      setExpandedStageId(stageId);
                      setExpandedProductByStage((prev) => ({
                        ...prev,
                        [stageId]: productId,
                      }));
                    }}
                    onStageDuplicated={(newStageId) => {
                      setExpandedStageId(newStageId);
                    }}
                    onDeleteStage={stages.length > 1 ? handleDeleteStage : undefined}
                  />
                </MDBox>
              ))}

              {!isReadOnly && (
                <MDBox display="flex" justifyContent="flex-end" gap="8px" p="12px">
                  {stages.length > 1 && (
                    <UiButton
                      uiVariant="primary-grey"
                      startIcon={<Icon>swap_vert</Icon>}
                      onClick={reorderDrawer.onOpen}
                    >
                      Reorder Jobs
                    </UiButton>
                  )}
                  <UiButton
                    uiVariant="primary"
                    startIcon={<Icon>add</Icon>}
                    onClick={handleAddStage}
                  >
                    Add Job
                  </UiButton>
                </MDBox>
              )}

              <ReorderStagesDrawer
                open={reorderDrawer.open}
                onClose={reorderDrawer.onClose}
                stages={stages as ProposalStage[]}
                proposalId={proposalId ?? ""}
              />

              <MDBox mt={2} pb={1}>
                <UiButton
                  uiVariant="outlined"
                  component={Link}
                  to={getRoute("proposals.draft-classic", [["proposalId", proposalId ?? ""]])}
                >
                  Open classic draft page
                </UiButton>
              </MDBox>

              {!isReadOnly && <PendingChanges />}
            </MDBox>
          </ProposalContext.Provider>
        </ProposalFormsContextProvider>
      </DraftContext.Provider>
    </DashboardPage>
  );
}
